import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ChevronDown } from 'lucide-react'
import { BottomSheet } from '@/shared/ui/bottom-sheet'
import { SelectionOption } from '@/shared/ui/selection-list'
import { cn } from '@/shared/lib/utils'
import { useTelegram } from '@/shared/lib/telegram'

export type SortOption = 'popular' | 'price-asc' | 'price-desc' | 'newest'

export interface SortOptionConfig {
  value: SortOption
  label: string
}

const SORT_OPTIONS: SortOptionConfig[] = [
  { value: 'popular', label: 'По популярности' },
  { value: 'price-asc', label: 'Цена: по возрастанию' },
  { value: 'price-desc', label: 'Цена: по убыванию' },
  { value: 'newest', label: 'Сначала новые' },
]

export interface CatalogSortProps {
  currentSort?: SortOption
  onSortChange?: (sort: SortOption) => void
  asSheet?: boolean
}

/**
 * Сортировка каталога с унифицированным Bottom Sheet
 */
export function CatalogSort({
  currentSort,
  onSortChange,
  asSheet = true,
}: CatalogSortProps) {
  const navigate = useNavigate()
  const { hapticFeedback } = useTelegram()
  const [isOpen, setIsOpen] = useState(false)

  const currentOption = SORT_OPTIONS.find((opt) => opt.value === currentSort) || SORT_OPTIONS[0]

  const handleSortChange = (sort: SortOption) => {
    if (onSortChange) {
      onSortChange(sort)
    } else {
      navigate({
        search: (prev: Record<string, unknown>) => ({ ...prev, sort }),
      } as any)
    }
    setIsOpen(false)
  }

  if (asSheet) {
    return (
      <>
        <button
          onClick={() => {
            hapticFeedback?.impactOccurred('light')
            setIsOpen(true)
          }}
          className={cn(
            'flex-shrink-0 px-4 rounded-[14px]',
            'transition-all font-medium text-sm',
            'flex items-center gap-2 shadow-sm dark:shadow-md',
            'min-h-[44px]',
            'bg-white/80 backdrop-blur-sm border border-white/18 text-ink',
            'hover:bg-white/90 hover:border-primary/30',
            'dark:bg-dark-elevated dark:border-dark-border dark:text-white dark:hover:bg-dark-card',
            'active:scale-[0.98] transition-transform duration-100'
          )}
        >
          <span>{currentOption.label}</span>
          <ChevronDown className="h-4 w-4" strokeWidth={1.5} />
        </button>

        <BottomSheet isOpen={isOpen} onClose={() => setIsOpen(false)} title="Сортировка" height={45}>
          <div className="px-4 pt-2 pb-6 space-y-2">
            {SORT_OPTIONS.map((option) => (
              <SelectionOption
                key={option.value}
                selected={currentSort === option.value}
                onClick={() => handleSortChange(option.value)}
                label={option.label}
              />
            ))}
          </div>
        </BottomSheet>
      </>
    )
  }

  return (
    <select
      value={currentSort || 'popular'}
      onChange={(e) => handleSortChange(e.target.value as SortOption)}
      className="px-4 py-2 rounded-lg border border-gray-300 bg-white"
    >
      {SORT_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  )
}
